import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Fade,
  Slide,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Autocomplete,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import StatusIcon from "@mui/icons-material/Info";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useSelector } from "react-redux";
import profileImage from "assets/MRwall.png";
import ScoreDashboard from "components/ScoreDashboard";
import {
  useGetMrStatusRecordQuery,
  useUploadPrescriptionMutation,
  useUploadDrDataMutation,
  useGetDocRepQuery,
  useUploadChemistPobMutation,
} from "state/api";

const MAX_SIZE_MB = 2;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file.type.startsWith("image/") || file.size <= MAX_SIZE_BYTES) {
      resolve(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      let { width, height } = img;
      const maxDim = 1920;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      let quality = 0.9;
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (blob.size <= MAX_SIZE_BYTES || quality <= 0.1) {
              resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
            } else {
              quality = parseFloat((quality - 0.1).toFixed(1));
              tryCompress();
            }
          },
          "image/jpeg",
          quality
        );
      };
      tryCompress();
    };
    img.src = url;
  });

const CameraUpload = ({ onFileReady, onError, sx = {} }) => {
  const [cameraOpen, setCameraOpen] = useState(false);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const fileRef = useRef(null);

  const handleProcess = async (file) => {
    if (!file) return;
    const processed = await compressImage(file);
    if (processed.size > MAX_SIZE_BYTES) {
      onError(`File size exceeds ${MAX_SIZE_MB}MB limit`);
    } else {
      onFileReady(processed);
    }
  };

  const openCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = mediaStream;
      setCameraOpen(true);
    } catch {
      onError("Camera permission denied or camera not available");
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(async (blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
      closeCamera();
      await handleProcess(file);
    }, "image/jpeg", 0.92);
  };

  return (
    <>
      <Box display="flex" gap={1} sx={{ mt: 1, ...sx }}>
        <Button
          variant="contained"
          startIcon={<CameraAltIcon />}
          sx={{ flex: 1 }}
          onClick={openCamera}
        >
          Camera
        </Button>
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFileIcon />}
          sx={{ flex: 1 }}
        >
          Upload
          <input
            ref={fileRef}
            type="file"
            hidden
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleProcess(e.target.files[0])}
          />
        </Button>
      </Box>

      <Dialog
        open={cameraOpen}
        onClose={closeCamera}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            if (videoRef.current && streamRef.current) {
              videoRef.current.srcObject = streamRef.current;
            }
          },
        }}
      >
        <DialogContent sx={{ p: 0, bgcolor: "black", lineHeight: 0 }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", display: "block" }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button onClick={closeCamera} color="error" variant="outlined">
            Cancel
          </Button>
          <Button onClick={capturePhoto} variant="contained" startIcon={<CameraAltIcon />}>
            Capture Photo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// StatusRecords Component
const StatusRecords = ({
  openStatusList,
  records,
  filterStatus,
  setFilterStatus,
}) => {
  const handleStatusChange = (event, newStatus) => {
    if (newStatus !== null) {
      setFilterStatus(newStatus);
    }
  };

  const filteredRecords = Array.isArray(records)
    ? records.filter((status) => status.STATUS === filterStatus)
    : [];

  return (
    <Slide direction="left" in={openStatusList} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 300,
          height: "100vh",
          zIndex: 1000,
          overflowY: "auto",
          backgroundColor: "transparent",
          backdropFilter: "blur(50px)",
        }}
        p={2}
      >
        <Typography
          variant="h4"
          fontWeight={700}
          component="h2"
          color="text.primary"
          mb={2}
        >
          Status Records
        </Typography>

        <ToggleButtonGroup
          value={filterStatus}
          exclusive
          onChange={handleStatusChange}
          sx={{ marginBottom: 2 }}
        >
          <ToggleButton value="APPROVED" color="success">
            Approved
          </ToggleButton>
          <ToggleButton value="REJECTED" color="error">
            Rejected
          </ToggleButton>
          <ToggleButton value="PENDING" color="warning">
            Pending
          </ToggleButton>
        </ToggleButtonGroup>

        <Grid container spacing={1}>
          {filteredRecords.length > 0 ? (
            filteredRecords.map((status, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={1}
                  sx={{
                    padding: 1,
                    backgroundColor: "transparent",
                    backdropFilter: "blur(50px)",
                  }}
                >
                  <Typography variant="body1">
                    {`Dr Name: ${status.DoctName}`} <br />
                    {`Submit Date: ${new Date(status.SUBDATE).toLocaleDateString("en-GB")}`}{" "}
                    <br />
                    {`Status: ${status.STATUS}`}
                  </Typography>
                </Paper>
              </Grid>
            ))
          ) : (
            <Typography variant="body1" color="text.secondary">
              No records available for{" "}
              {filterStatus?.toLowerCase() || "selected"} status.
            </Typography>
          )}
        </Grid>
      </Box>
    </Slide>
  );
};








// Main Component
export default function Mr() {
  const [doctCatId, setDoctCatId] = useState(null);
  const [hqMstId, setHqMstId] = useState(null);
  const [isPrescription, setIsPrescription] = useState(true);
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [openShutter, setOpenShutter] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const [mclCode, setMclCode] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openStatusList, setOpenStatusList] = useState(false);
  const [filterStatus, setFilterStatus] = useState("APPROVED");
  const [selectedOption, setSelectedOption] = useState("Finease");
  const [selectedTab, setSelectedTab] = useState("PRESCRIPTION");

  const MgCd = useSelector((state) => state.global.MgCd);
  const EmpNo = useSelector((state) => state.global.EmpNo);

  const { data: apiResponse = { data: [] }, error } = useGetDocRepQuery(MgCd);
  const docReps = useMemo(() => apiResponse.data || [], [apiResponse.data]);
  const [uploadPrescription] = useUploadPrescriptionMutation();
  const [uploadDoctorData] = useUploadDrDataMutation();
  const { data: mrStatusResponse } = useGetMrStatusRecordQuery(EmpNo);
  const mrStatusRecord = mrStatusResponse?.data || [];
  const [fileError, setFileError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [invoice, setInvoice] = useState({
    itemName: "",
    strips: "",
    chemistName: "",
    chemistAddress: "",
    dlNo: "",
    state: "",
    email: "",
    phone: "",
  });
  const [invoiceFileError, setInvoiceFileError] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);

  console.log(invoice)

  const [uploadInvoice] = useUploadChemistPobMutation();

  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      if (/^\d{0,10}$/.test(value)) {
        setInvoice((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setInvoice((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleInvoiceClear = () => {
    setInvoice({
      strips: "",
      chemistName: "",
      chemistAddress: "",
      dlNo: "",
      state: "",
      email: "",
      phone: "",
    });
    setInvoiceFile(null);
    setInvoiceFileError("");
  };

  const handleInvoiceFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_SIZE_BYTES) {
        setInvoiceFileError(`File size should be less than ${MAX_SIZE_MB}MB`);
        setInvoiceFile(null);
        setSnackbarMessage(`File size exceeds ${MAX_SIZE_MB}MB limit`);
        setOpenSnackbar(true);
      } else {
        setInvoiceFileError(null);
        setInvoiceFile(file);
      }
    }
  };

  useEffect(() => {
    if (error) {
      console.error("Error fetching document representations:", error);
      setSnackbarMessage("Failed to fetch document representations.");
      setOpenSnackbar(true);
    }
  }, [error]);

  useEffect(() => {
    if (docReps.length > 0) {
      setHqMstId(docReps[0].HqMstId);
    }
  }, [docReps]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_SIZE_BYTES) {
        setFileError(`File size should be less than ${MAX_SIZE_MB}MB`);
        setPrescriptionFile(null);
        setSnackbarMessage(`File size exceeds ${MAX_SIZE_MB}MB limit`);
        setOpenSnackbar(true);
      } else {
        setFileError(null);
        setPrescriptionFile(file);
      }
    }
  };

  const productOptions = {
    Finease: ["Finease"],
    "Queezy-ER": ["Queezy-ER"],
    Trivitalin: ["Trivitalin"],
    Hemozink: ["Hemozink"],
    "Sudin Cold": ["Sudin Cold"],
  };

  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      setSelectedOption(newValue);
    }
  };

  const handleCloseShutter = () => {
    setOpenShutter(false);
  };

  const handleToggleChange = (event, newAlignment) => {
    if (newAlignment !== null) {
      setIsPrescription(newAlignment === "prescription");
      resetFields(newAlignment);
    }
  };

  const resetFields = (newAlignment) => {
    if (newAlignment === "prescription") {
      setSelectedDoctor("");
      setSelectedSpecialty("");
      setMclCode("");
      setPrescriptionFile(null);
    } else {
      setSelectedDoctor("");
      setSelectedSpecialty("");
    }
  };

  const handlePrescriptionSubmit = async (event) => {
    event.preventDefault();
    if (
      !selectedDoctor ||
      !selectedSpecialty ||
      !mclCode ||
      !MgCd ||
      !EmpNo
    ) {
      setSnackbarMessage(
        "Please fill in all required fields."
      );
      setOpenSnackbar(true);
      return;
    }

    const prescriptionFormData = new FormData();
    prescriptionFormData.append("product", selectedOption);
    prescriptionFormData.append("doctorName", selectedDoctor.name);
    prescriptionFormData.append("specialty", selectedSpecialty);
    prescriptionFormData.append("mclCode", mclCode);
    if (prescriptionFile) prescriptionFormData.append("prescription", prescriptionFile);
    prescriptionFormData.append("MgCd", MgCd);
    prescriptionFormData.append("EmpNo", EmpNo);

    try {
      await uploadPrescription(prescriptionFormData).unwrap();
      setSnackbarMessage("Prescription uploaded successfully!");
      setOpenSnackbar(true);
      resetFields("prescription");
    } catch (error) {
      console.error("Error uploading prescription:", error);
      setSnackbarMessage("Failed to upload prescription.");
      setOpenSnackbar(true);
    }
  };

  const handleInvoiceSubmit = async (event) => {
    event.preventDefault();
    if (
      !selectedDoctor ||
      !selectedSpecialty ||
      !mclCode ||
      !invoice.itemName ||
      !invoice.strips ||
      !invoice.chemistName ||
      !invoice.chemistAddress ||
      !invoice.dlNo ||
      !invoice.state ||
      !invoice.email ||
      !invoice.phone ||
      !invoiceFile ||
      !MgCd ||
      !EmpNo
    ) {
      setSnackbarMessage(
        "Please fill in all required fields and upload the invoice."
      );
      setOpenSnackbar(true);
      return;
    }

    const invoiceFormData = new FormData();
    invoiceFormData.append("doctorName", selectedDoctor.name);
    invoiceFormData.append("specialty", selectedSpecialty);
    invoiceFormData.append("mclCode", mclCode);
    invoiceFormData.append("itemName", invoice.itemName);
    invoiceFormData.append("strips", invoice.strips);
    invoiceFormData.append("chemistName", invoice.chemistName);
    invoiceFormData.append("chemistAddress", invoice.chemistAddress);
    invoiceFormData.append("dlNo", invoice.dlNo);
    invoiceFormData.append("state", invoice.state);
    invoiceFormData.append("email", invoice.email);
    invoiceFormData.append("mobile", invoice.phone);
    invoiceFormData.append("invoiceFile", invoiceFile);
    invoiceFormData.append("MgCd", MgCd);
    invoiceFormData.append("EmpNo", EmpNo);

    try {
      await uploadInvoice(invoiceFormData).unwrap();
      setSnackbarMessage("Invoice uploaded successfully!");
      setOpenSnackbar(true);
      handleInvoiceClear();
    } catch (error) {
      console.error("Error uploading invoice:", error);
      setSnackbarMessage("Failed to upload invoice.");
      setOpenSnackbar(true);
    }
  };

  const handleDoctorSubmit = async (event) => {
    event.preventDefault();
    if (!selectedDoctor || !selectedSpecialty) {
      setSnackbarMessage("Please fill in all required fields.");
      setOpenSnackbar(true);
      return;
    }

    const doctorData = {
      MgCd: MgCd,
      HqMstId: hqMstId,
      doctorName: selectedDoctor.name,
      DoctCatId: doctCatId,
      UpdatedByEmpNo: EmpNo,
    };

    try {
      await uploadDoctorData(doctorData).unwrap();
      setSnackbarMessage("Doctor created successfully!");
      setOpenSnackbar(true);
      resetFields("doctor");
    } catch (error) {
      console.error("Error creating doctor:", error);
      setSnackbarMessage("Failed to create doctor.");
      setOpenSnackbar(true);
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const toggleStatusList = () => {
    setOpenStatusList((prev) => !prev);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        backgroundImage: `url(${profileImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        p: 3,
        overflow: "auto",
      }}
    >
      <IconButton
        onClick={toggleStatusList}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 1000,
          color: "#80bf1b",
        }}
      >
        <StatusIcon />
      </IconButton>

      {/* StatusRecords Component */}
      <StatusRecords
        openStatusList={openStatusList}
        records={mrStatusRecord}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
      />

      <div className="flex gap-2 mb-4">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              sx={{
                borderRadius: "9999px",
                px: 6,
                py: 2,
                fontSize: "0.875rem",
                fontWeight: "bold",
                boxShadow: 3,
                transition: "all 0.3s",
                ...(selectedTab === "PRESCRIPTION" && {
                  bgcolor: "primary.main",
                  color: "white",
                  transform: "scale(1.05)",
                }),
                ...(selectedTab !== "PRESCRIPTION" && {
                  bgcolor: "grey.100",
                  color: "grey.700",
                  "&:hover": {
                    bgcolor: "grey.200",
                  },
                }),
              }}
              onClick={() => setSelectedTab("PRESCRIPTION")}
            >
              IPL
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: "9999px",
                px: 6,
                py: 2,
                fontSize: "0.875rem",
                fontWeight: "bold",
                boxShadow: 3,
                transition: "all 0.3s",
                ...(selectedTab === "INVOICE" && {
                  bgcolor: "primary.main",
                  color: "white",
                  transform: "scale(1.05)",
                }),
                ...(selectedTab !== "INVOICE" && {
                  bgcolor: "grey.100",
                  color: "grey.700",
                  "&:hover": {
                    bgcolor: "grey.200",
                  },
                }),
              }}
              onClick={() => setSelectedTab("INVOICE")}
            >
              STAR CHEMIST
            </Button>
          </Box>
        </Box>
      </div>

      {selectedTab === "PRESCRIPTION" && (
        <div>
          <Paper
            elevation={3}
            sx={{
              width: "100%",
              maxWidth: 400,
              backgroundColor: "transparent",
              padding: 3,
              backdropFilter: "blur(50px)",
            }}
          >
            <Autocomplete
              options={Object.keys(productOptions)}
              value={selectedOption}
              onChange={handleChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Product"
                  variant="outlined"
                  margin="normal"
                  required
                />
              )}
              sx={{ mb: 2 }}
            />

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h5" component="h2" color="text.primary">
                {isPrescription ? "Prescription Upload" : "Create Doctor"}
              </Typography>
              <ToggleButtonGroup
                value={isPrescription ? "prescription" : "doctor"}
                exclusive
                onChange={handleToggleChange}
                aria-label="upload type"
              >
                <ToggleButton value="prescription" aria-label="prescription upload">
                  Prescription
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <form
              onSubmit={isPrescription ? handlePrescriptionSubmit : handleDoctorSubmit}
              encType="multipart/form-data"
            >
              {isPrescription ? (
                <>
                  <TextField
                    fullWidth
                    label="Product"
                    variant="outlined"
                    margin="normal"
                    required
                    value={selectedOption}
                    InputProps={{ readOnly: true }}
                  />
                  <input type="hidden" name="product" value={selectedOption} />

                  <Autocomplete
                    options={[...new Set(docReps.map((doc) => doc.DocCategory))]}
                    value={selectedSpecialty}
                    onChange={(event, newValue) => {
                      setSelectedSpecialty(newValue || "");
                      setSelectedDoctor(null);
                      setMclCode("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Specialty"
                        variant="outlined"
                        margin="normal"
                        required
                      />
                    )}
                  />

                  <Autocomplete
                    options={docReps
                      .filter((doc) => doc.DocCategory === selectedSpecialty)
                      .map((doc) => ({ name: doc.DoctName, code: doc.DoctCd }))}
                    getOptionLabel={(option) => option.name || ""}
                    value={selectedDoctor}
                    onChange={(event, newValue) => {
                      setSelectedDoctor(newValue);
                      setMclCode(newValue ? newValue.code : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Dr. Name"
                        variant="outlined"
                        margin="normal"
                        required
                      />
                    )}
                  />

                  <TextField
                    fullWidth
                    label="MCL Code"
                    variant="outlined"
                    margin="normal"
                    required
                    value={mclCode}
                    InputProps={{ readOnly: true }}
                  />
                  <input type="hidden" name="mclCode" value={mclCode} />

                  <CameraUpload
                    onFileReady={(file) => { setPrescriptionFile(file); setFileError(null); }}
                    onError={(msg) => { setFileError(msg); setPrescriptionFile(null); setSnackbarMessage(msg); setOpenSnackbar(true); }}
                  />

                  {fileError && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {fileError}
                    </Typography>
                  )}

                  <Fade in={!!prescriptionFile && !fileError} timeout={500}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ marginTop: 1 }}
                    >
                      {prescriptionFile
                        ? `${prescriptionFile.name} (${(prescriptionFile.size / (1024 * 1024)).toFixed(2)} MB)`
                        : ""}
                    </Typography>
                  </Fade>

                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={{ marginTop: 2 }}
                    type="submit"
                    disabled={!!fileError}
                  >
                    Submit
                  </Button>
                </>
              ) : (
                <>
                  <Autocomplete
                    options={[...new Set(docReps.map((doc) => doc.DocCategory))]}
                    value={selectedSpecialty}
                    onChange={(event, newValue) => {
                      setSelectedSpecialty(newValue || "");
                      const selectedDoc = docReps.find(
                        (doc) => doc.DocCategory === newValue
                      );
                      setDoctCatId(selectedDoc ? selectedDoc.DoctCatId : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Specialty"
                        variant="outlined"
                        fullWidth
                        required
                        sx={{ marginTop: 2 }}
                      />
                    )}
                  />

                  <TextField
                    label="New Doctor Name"
                    variant="outlined"
                    fullWidth
                    value={selectedDoctor.name}
                    onChange={(e) => setSelectedDoctor({ name: e.target.value })}
                    required
                    sx={{ marginTop: 2 }}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={{ marginTop: 2 }}
                    type="submit"
                  >
                    Submit
                  </Button>
                </>
              )}
            </form>

            <Snackbar
              open={openSnackbar}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              message={snackbarMessage}
            />
          </Paper>
        </div>
      )}

      {selectedTab === "INVOICE" && (
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: 400,
            backgroundColor: "transparent",
            padding: 3,
            mb: 10,
            backdropFilter: "blur(50px)",
          }}
        >
          <Typography variant="h5" component="h2" color="text.primary" mb={2}>
            Invoice Upload
          </Typography>

          <form onSubmit={handleInvoiceSubmit} encType="multipart/form-data">
            <Autocomplete
              options={[...new Set(docReps.map((doc) => doc.DocCategory))]}
              value={selectedSpecialty}
              onChange={(event, newValue) => {
                setSelectedSpecialty(newValue || "");
                setSelectedDoctor(null);
                setMclCode("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Specialty"
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                />
              )}
            />

            <Autocomplete
              options={docReps
                .filter((doc) => doc.DocCategory === selectedSpecialty)
                .map((doc) => ({ name: doc.DoctName, code: doc.DoctCd }))}
              getOptionLabel={(option) => option.name || ""}
              value={selectedDoctor}
              onChange={(event, newValue) => {
                setSelectedDoctor(newValue);
                setMclCode(newValue ? newValue.code : "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dr. Name"
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                />
              )}
            />

            <TextField
              fullWidth
              label="MCL Code"
              variant="outlined"
              margin="normal"
              required
              value={mclCode}
              InputProps={{ readOnly: true }}
            />
            <input type="hidden" name="mclCode" value={mclCode} />


            <Autocomplete
              options={Object.keys(productOptions)}
              value={invoice.itemName}
              onChange={(event, newValue) => {
                setInvoice((prev) => ({ ...prev, itemName: newValue || "" }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Product"
                  variant="outlined"
                  margin="normal"
                  required
                />
              )}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="No. of Strips"
              variant="outlined"
              margin="normal"
              name="strips"
              value={invoice.strips}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              fullWidth
              label="Chemist Name"
              variant="outlined"
              margin="normal"
              name="chemistName"
              value={invoice.chemistName}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              fullWidth
              label="Chemist Address"
              variant="outlined"
              margin="normal"
              name="chemistAddress"
              value={invoice.chemistAddress}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              fullWidth
              label="DL Number"
              variant="outlined"
              margin="normal"
              name="dlNo"
              value={invoice.dlNo}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              fullWidth
              label="State"
              variant="outlined"
              margin="normal"
              name="state"
              value={invoice.state}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              variant="outlined"
              margin="normal"
              name="email"
              value={invoice.email}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              label="Phone Number"
              variant="outlined"
              fullWidth
              name="phone"
              value={invoice.phone}
              onChange={handleInvoiceChange}
              error={invoice.phone.length > 0 && invoice.phone.length !== 10}
              helperText={
                invoice.phone.length > 0 && invoice.phone.length !== 10
                  ? "Phone number must be exactly 10 digits"
                  : ""
              }
              required
            />

            <CameraUpload
              onFileReady={(file) => { setInvoiceFile(file); setInvoiceFileError(""); }}
              onError={(msg) => { setInvoiceFileError(msg); setInvoiceFile(null); setSnackbarMessage(msg); setOpenSnackbar(true); }}
              sx={{ mt: 2 }}
            />

            {invoiceFileError && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {invoiceFileError}
              </Typography>
            )}

            <Fade in={!!invoiceFile} timeout={500}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ marginTop: 1 }}
              >
                {invoiceFile
                  ? `${invoiceFile.name} (${(invoiceFile.size / (1024 * 1024)).toFixed(2)} MB)`
                  : ""}
              </Typography>
            </Fade>

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ marginRight: 1 }}
                onClick={handleInvoiceClear}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                color="success"
                fullWidth
                type="submit"
                disabled={invoice.phone?.length !== 10}
              >
                Submit
              </Button>
            </Box>
          </form>

          <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            message={snackbarMessage}
          />
        </Paper>
      )}
    </Box>
  );
}
